import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  trialNewsService,
  type TrialNewsLatestResponse,
} from "@/services/trialNewsService";

const POLL_INTERVAL_MS = 5000;

// Latest completed report + any in-flight run for a trial+role. While a run
// is in flight the query polls until the backend marks it complete, at which
// point `latest` carries the fresh report and `active_run` clears.
export const useTrialNewsLatest = (
  trialId: string,
  role: string,
  enabled: boolean,
) => {
  return useQuery<TrialNewsLatestResponse>({
    queryKey: ["trial-news", trialId, role],
    queryFn: () => trialNewsService.getLatest(trialId, role),
    enabled: enabled && !!trialId && !!role,
    refetchInterval: (query) =>
      query.state.data?.active_run ? POLL_INTERVAL_MS : false,
    staleTime: 60 * 1000,
  });
};

export const useTrialNewsRefresh = (trialId: string, role: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => trialNewsService.refresh(trialId, role),
    onSuccess: () => {
      // Re-fetch so the latest query picks up the active run and starts polling.
      queryClient.invalidateQueries({
        queryKey: ["trial-news", trialId, role],
      });
    },
  });
};
